import { Popover } from '@mui/material';
import { isEqual, sortBy } from 'lodash';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import CrossIcon from '../../../assets/svg/CrossIcon';
import GlassIcon from '../../../assets/svg/GlassIcon';
import { Button, Checkbox, IconButton } from '../../Shared';

export const ClientsFilter = ({
  clientIds,
  onChangeClientIds,
  clients,
  isClientsLoading,
  isClientsError,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedClientsIds, setSelectedClientsIds] = useState(clientIds);

  const onChangeSelectedClientsIds = tenantID => {
    setSelectedClientsIds(prev => {
      const isSelected = prev.includes(tenantID);

      if (isSelected) return prev.filter(v => v !== tenantID);

      return [...prev, tenantID];
    });
  };

  const onChange = () => {
    onChangeClientIds(prev => {
      return {
        ...prev,
        clientIds: selectedClientsIds,
      };
    });
    onClosePopover();
  };

  const onSelectAll = () => {
    setSelectedClientsIds(prev => {
      const nextClientIds = clients.map(({ TenantID }) => TenantID);

      return nextClientIds.length === prev.length ? [] : nextClientIds;
    });
  };

  const reset = () => {
    onChangeClientIds(prev => {
      return {
        ...prev,
        clientIds: [],
      };
    });
  };

  const onChangeSearchValue = e => {
    const nextSearchValue = e.target.value;
    setSearchValue(nextSearchValue);
  };

  const resetSearchValue = () => {
    setSearchValue('');
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setSearchValue('');
    setSelectedClientsIds(clientIds);
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const hasSearchValue = !!searchValue;
  const filteredClients = clients.filter(({ TenantName }) => {
    const searchValueToLowerCase = searchValue.toLowerCase();
    const tenantNameToLowerCase = TenantName.toLowerCase();
    return tenantNameToLowerCase.includes(searchValueToLowerCase);
  });
  const hasClients = filteredClients.length > 0;
  const hasSelectedCallerIds = clientIds.length > 0;
  const isButtonApplyDisabled = isEqual(
    sortBy(selectedClientsIds),
    sortBy(clientIds),
  );

  if (isClientsError) return null;

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] px-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          hasSelectedCallerIds && 'bg-brand-900 text-white !pl-6 !pr-5',
        )}
        onClick={onOpenPopover}
        disabled={isClientsLoading}
      >
        {hasSelectedCallerIds ? `Client +${clientIds.length}` : 'Client'}
        {hasSelectedCallerIds && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              '!rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white !max-w-[320px] !w-full max-h-[80vh] overflow-hidden flex flex-col',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="overflow-hidden flex flex-col max-h-[672px]">
          <div className="p-1">
            <div className="relative">
              <GlassIcon className="w-5 h-5 text-brand-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                value={searchValue}
                onChange={onChangeSearchValue}
                placeholder="Search"
                className="w-full py-2 pl-11 pr-[38px] text-[16px]/[24px] text-base-1000 border-none placeholder:text-base-800 focus:outline-none"
              />
              {hasSearchValue && (
                <IconButton
                  buttonProps={{
                    className:
                      'w-6 h-6 !absolute right-3 top-1/2 -translate-y-1/2',
                    onClick: resetSearchValue,
                  }}
                  iconProps={{
                    name: 'cross',
                    className: 'h-4',
                  }}
                />
              )}
            </div>
          </div>
          {hasClients ? (
            <div className="flex-1 overflow-auto max-h-[552px]">
              <div className="flex flex-col gap-1 p-1">
                {filteredClients.map(({ TenantID, TenantName }) => {
                  const checked = selectedClientsIds.includes(TenantID);

                  return (
                    <label
                      key={TenantID}
                      className="flex items-center gap-2 pl-1 py-1.5 cursor-pointer w-full rounded-lg hover:bg-brand-300/[0.08] truncate"
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => {
                          onChangeSelectedClientsIds(TenantID);
                        }}
                      />
                      <span className="text-[16px]/[24px] text-base-1000 font-medium truncate">
                        {TenantName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex p-4">
              <span className="w-full text-[14px]/[20px] text-base-800 text-center">
                No results found
              </span>
            </div>
          )}
          <div className="flex gap-3 justify-end px-4 py-3 border-t border-t-base-200">
            <Button
              title="Select All"
              variant="secondary"
              onClick={onSelectAll}
              disabled={hasSearchValue}
            />
            <Button
              title="Apply"
              onClick={onChange}
              disabled={isButtonApplyDisabled}
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
